import React, { useState } from 'react';
import styled from 'styled-components';
import { FaTimes, FaPaperPlane, FaSpinner } from 'react-icons/fa';
import { Icon } from './Icon';
import { config } from '../../config';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1100;
`;

const ModalContent = styled.div`
  background: #2a2a2a;
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 28rem;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid #444;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #444;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: bold;
  color: #ffffff;
  margin: 0;
`;

const CloseButton = styled.button`
  color: #cccccc;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 8px;
  transition: background-color 0.2s;
  
  &:hover {
    color: #ffffff;
    background-color: #333;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Form = styled.form`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #cccccc;
  margin-bottom: 0.5rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #444;
  border-radius: 8px;
  font-size: 0.875rem;
  background: #1a1a1a;
  color: #ffffff;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  option {
    background: #1a1a1a;
    color: #ffffff;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #444;
  border-radius: 8px;
  font-size: 0.875rem;
  background: #1a1a1a;
  color: #ffffff;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &::placeholder {
    color: #666;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #444;
  border-radius: 8px;
  font-size: 0.875rem;
  resize: none;
  background: #1a1a1a;
  color: #ffffff;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &::placeholder {
    color: #666;
  }
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' }>`
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  
  ${props => props.type === 'success' && `
    background-color: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #166534;
  `}
  
  ${props => props.type === 'error' && `
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
  `}
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  padding-top: 1rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  ${props => props.variant === 'primary' ? `
    background-color: #007bff;
    color: white;
    border: 1px solid #007bff;
    
    &:hover:not(:disabled) {
      background-color: #0056b3;
    }
  ` : `
    background-color: #444;
    color: #ffffff;
    border: 1px solid #444;
    
    &:hover:not(:disabled) {
      background-color: #555;
    }
  `}
`;

const SpinnerWrapper = styled.div`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({ isOpen, onClose }) => {
  const [feedbackType, setFeedbackType] = useState('General');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const feedbackTypes = [
    'General',
    'Bug Report',
    'Feature Request',
    'Content Suggestion',
    'Technical Issue',
    'Subscription / Billing Issue',
    'Other'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subject.trim() || !message.trim()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          feedbackType,
          subject: subject.trim(),
          message: message.trim(),
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset form
        setFeedbackType('General');
        setSubject('');
        setMessage('');
        // Close form after a brief delay
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setSubmitStatus('idle');
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleBackdropClick}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Send Feedback</ModalTitle>
          <CloseButton
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <Icon icon={FaTimes} size={20} />
          </CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="feedbackType">
              Type of Feedback
            </Label>
            <Select
              id="feedbackType"
              value={feedbackType}
              onChange={(e) => setFeedbackType(e.target.value)}
              disabled={isSubmitting}
            >
              {feedbackTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="subject">
              Subject
            </Label>
            <Input
              type="text"
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isSubmitting}
              placeholder="Brief description of your feedback"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="message">
              Message
            </Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isSubmitting}
              placeholder="Please provide detailed feedback..."
              rows={6}
              required
            />
          </FormGroup>

          {submitStatus === 'success' && (
            <StatusMessage type="success">
              Thank you! Your feedback has been sent successfully.
            </StatusMessage>
          )}

          {submitStatus === 'error' && (
            <StatusMessage type="error">
              Sorry, there was an error sending your feedback. Please try again.
            </StatusMessage>
          )}

          <ButtonGroup>
            <Button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !subject.trim() || !message.trim()}
              variant="primary"
            >
              {isSubmitting ? (
                <>
                  <SpinnerWrapper>
                    <Icon icon={FaSpinner} size={16} />
                  </SpinnerWrapper>
                  Sending...
                </>
              ) : (
                <>
                  <Icon icon={FaPaperPlane} size={16} />
                  Send Feedback
                </>
              )}
            </Button>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default FeedbackForm; 